import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ANY_SPORT_SERIES_RAFFLE } from '@sorare/core/src/constants/__generated__/routes.ts';
import { useSportContext } from '@sorare/core/src/contexts/sport';

import { FeaturedMissionCard } from '#src/components/missions/FeaturedMissionCard/index.tsx';
import { TaskClaimedDialogs } from '#src/components/missions/TaskClaimedDialogs/index.tsx';
import { useTaskClaimedDialogs } from '#src/components/missions/TaskClaimedDialogs/useTaskClaimedDialogs.tsx';
import { RaffleTask } from '#src/components/series/raffle/RaffleTask/index.tsx';

import type { FeaturedRaffleTask_taskInterface } from './__generated__/index.graphql.ts';

type Props = {
  task?: FeaturedRaffleTask_taskInterface | null;
};

export const FeaturedRaffleTask = ({ task }: Props) => {
  const { generateSportContextPath } = useSportContext();
  const {
    onClaim,
    loadingTaskId,
    pickerDialogTask,
    setPickerDialogTask,
    revealPackCardsDialogReward,
    setRevealPackCardsDialogReward,
    rewardDialogTask,
    setRewardDialogTask,
  } = useTaskClaimedDialogs();

  return (
    <>
      <FeaturedMissionCard color="var(--c-raffle)">
        {!!task && (
          <RaffleTask
            task={task}
            onClaim={() => {
              onClaim(task);
            }}
            loading={loadingTaskId === task.id}
          />
        )}
        <Button
          size="medium"
          color="quaternary"
          to={{
            pathname: generateSportContextPath(ANY_SPORT_SERIES_RAFFLE),
            search: '?tab=missions',
          }}
        >
          <FormattedMessage
            defaultMessage="View all Raffle Missions"
            description="FeaturedRaffleTask - View all Raffle Missions button"
          />
        </Button>
      </FeaturedMissionCard>
      <TaskClaimedDialogs
        pickerDialogTask={pickerDialogTask}
        onPickerDialogClose={() => {
          setPickerDialogTask(null);
        }}
        revealPackCardsDialogReward={revealPackCardsDialogReward}
        onRevealPackCardsDialogClose={() => {
          setRevealPackCardsDialogReward(null);
        }}
        rewardDialogTask={rewardDialogTask}
        onRewardDialogClose={() => {
          setRewardDialogTask(null);
        }}
      />
    </>
  );
};

FeaturedRaffleTask.fragments = {
  taskInterface: gql`
    fragment FeaturedRaffleTask_taskInterface on TaskInterface {
      id
      ...RaffleTask_taskInterface
      ...TaskClaimedDialogs_taskInterface
    }
    ${RaffleTask.fragments.taskInterface}
    ${TaskClaimedDialogs.fragments.taskInterface}
  ` as TypedDocumentNode<FeaturedRaffleTask_taskInterface>,
};
